import { Button } from '@aod/ui/components/ui/button';
import { SceneDialog } from '@aod/ui/components/ui/scene-dialog';
import { HelpCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { type MessageKey, useT } from '../i18n/index.js';
import { rich } from '../i18n/rich.js';
import { appDom } from '../lib/appDom.js';

// [key label, description]. Key names that are literal keycaps (Esc, `,
// Enter) aren't translated; see KBD_LITERALS.
const CONTROLS: ReadonlyArray<readonly [MessageKey, MessageKey]> = [
  ['help.key.clickTile', 'help.key.clickTileDesc'],
  ['help.key.clickCity', 'help.key.clickCityDesc'],
  ['help.key.clickUnit', 'help.key.clickUnitDesc'],
  ['help.key.rightClick', 'help.key.rightClickDesc'],
  ['keys.esc', 'keys.esc'],
  ['keys.debug', 'keys.debug'],
  ['keys.chat', 'keys.chat'],
];

const KBD_LITERALS: Partial<Record<MessageKey, string>> = {
  'keys.esc': 'Esc',
  'keys.debug': '`',
  'keys.chat': 'Enter',
};

// [name, description, colour]. Colours darken in light mode for contrast.
const RESOURCES: ReadonlyArray<readonly [MessageKey, MessageKey, string]> = [
  ['help.res.food', 'help.res.foodDesc', 'text-green-700 dark:text-green-400'],
  ['help.res.industry', 'help.res.industryDesc', 'text-blue-700 dark:text-blue-400'],
  ['help.res.knowledge', 'help.res.knowledgeDesc', 'text-amber-700 dark:text-amber-400'],
  ['help.res.faith', 'help.res.faithDesc', 'text-purple-700 dark:text-purple-400'],
  ['help.res.gold', 'help.res.goldDesc', 'text-yellow-700 dark:text-yellow-400'],
];

const VICTORY_PATHS = ['conquest', 'economy', 'science', 'faith', 'diplomacy'] as const;

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="px-1.5 py-0.5 rounded bg-secondary text-[11px] font-mono text-foreground">
      {children}
    </kbd>
  );
}

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
}

export function HelpModal({ open, onClose }: Props) {
  const t = useT();
  if (!open) return null;

  return (
    <SceneDialog
      size="lg"
      onClose={onClose}
      icon={<HelpCircle className="size-5 text-primary" aria-hidden />}
      title={t('help.title')}
      data-testid="help-modal"
      {...appDom({ id: 'help-modal', type: 'Modal', scope: 'dialog', label: t('help.label') })}
      footer={
        <div className="flex w-full items-center justify-between">
          <span className="text-[11px] text-muted-foreground">
            {t('help.fullGuide')} <code className="text-[11px]">docs/PLAYER_GUIDE.md</code>
          </span>
          <Button type="button" variant="secondary" size="sm" onClick={onClose} className="text-xs">
            {t('help.gotIt')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 text-xs">
        <section>
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('help.controls')}</h3>
          <div className="space-y-1.5 text-muted-foreground">
            {CONTROLS.map(([key, desc]) => (
              <p key={key}>
                <Kbd>{KBD_LITERALS[key] ?? t(key)}</Kbd> — {t(desc)}
              </p>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('help.resources')}</h3>
          <div className="space-y-1 text-muted-foreground">
            {RESOURCES.map(([name, desc, tone]) => (
              <p key={name}>
                <span className={`${tone} font-medium`}>{t(name)}</span> — {t(desc)}
              </p>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('help.combat')}</h3>
          <div className="space-y-1 text-muted-foreground">
            <p>{t('help.combat.tip1')}</p>
            <p>{t('help.combat.tip2')}</p>
            <p>
              {rich(t('help.combat.tip3'), {
                hills: <strong className="text-foreground">{t('help.combat.hills')}</strong>,
                mountains: (
                  <strong className="text-foreground">{t('help.combat.mountains')}</strong>
                ),
                cities: <strong className="text-foreground">{t('help.combat.cities')}</strong>,
              })}
            </p>
            <p>{t('help.combat.tip4')}</p>
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-foreground mb-2">{t('help.victory')}</h3>
          <div className="space-y-0.5 text-muted-foreground">
            {VICTORY_PATHS.map((path) => (
              <p key={path}>
                <strong className="text-foreground">{t(`victory.path.${path}`)}</strong> —{' '}
                {t(`help.victory.${path}`)}
              </p>
            ))}
          </div>
        </section>
      </div>
    </SceneDialog>
  );
}
